import React, { useEffect, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import styled, { keyframes } from "styled-components/macro";
import { mediaMin, sizes } from "styles/media";
import projectImageSrc from "../project.png";
import contractImageSrc from "./contract-long.png";
import inquiryImageSrc from "./inquiry.png";
import kickoffMeetingImageSrc from "./meeting-note.png";

const DESKTOP_SCROLL_MULTIPLES = 1.5; // 각 애니메이션 단계 별로 얼마나 스크롤을 해야 하는지 정하는 변수
const MOBILE_SCROLL_MULTIPLES = 1; // 모바일에서는 데스크탑보다 각 스텝 별 스크롤 간격을 작게 한다.

const Wrapper = styled.section`
  width: 100%;
  height: ${10 * 100 * MOBILE_SCROLL_MULTIPLES +
  100}vh; // client experience section 으로 넘어가기 직전에 스크롤 패딩을 주기 위해 높이 설정을 더 크게 함
  ${mediaMin.xs`
    height: ${10 * 100 * DESKTOP_SCROLL_MULTIPLES + 100}vh;
  `}
`;

const Page = styled.div`
  display: flex;
  width: 100%;
  height: 100vh;
  position: sticky;
  top: 0;
  overflow: hidden;
`;

const MainCopy = styled.div`
  width: 100%;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 101;
  font-size: 24px;
  font-weight: 500;
  line-height: 1.4;
  text-align: center;
  padding: 0 24px;

  ${mediaMin.xs`
    font-size: 44px;
  `}

  ${mediaMin.sm`
    font-size: 48px;
  `}

  ${mediaMin.md`
    font-size: 52px;
  `}

  ${mediaMin.lg`
    font-size: 58px;
  `}
`;

const ImgCover = styled.div`
  width: calc(100% + 20px);
  height: 50%;
  background-color: ${({ theme }) => theme.background};
  position: absolute;
  left: -10px;
  z-index: 1;
`;

const ImgTopCover = styled(ImgCover)`
  top: 0;
`;

const ImgBottomCover = styled(ImgCover)`
  bottom: 0;
`;

const ProjectImg = styled.img`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  opacity: 1;

  ${mediaMin.xs`
    width: calc(100% - 60px);
    max-width: 600px;
  `}

  ${mediaMin.sm`
    max-width: 680px;
  `}

  ${mediaMin.lg`
    max-width: 720px;
  `}
`;

const PhaseImg = styled.img`
  position: absolute;
  width: 100%;
  opacity: 1;
`;

const contractScrollAnimation = keyframes`
  0% {
    bottom: -320%;
  }
  50% {
    bottom: -160%;
  }
  100% {
    bottom: 0;
  }
`;

const kickoffMeetingScrollAnimation = keyframes`
  0% {
    bottom: -128%;
  }
  50% {
    bottom: -64%;
  }
  100% {
    bottom: 0;
  }
`;

const RatioContainer = styled.div`
  position: absolute;
  top: 50%;
  border-radius: 8px;
  overflow: hidden;

  width: 220px;
  height: 264px;

  ${mediaMin.xs`
    width: 300px;
    height: 360px;
  `}

  ${mediaMin.sm`
    width: 320px;
    height: 380px;
  `}

  &.contract-scroll-animate {
    ${PhaseImg} {
      animation: 4s ${contractScrollAnimation};
      animation-delay: 0.5s;
    }
  }

  &.contract-scroll-animate-end {
    ${PhaseImg} {
      bottom: 0;
    }
  }

  &.kickoff-meeting-scroll-animate {
    ${PhaseImg} {
      animation: 4s ${kickoffMeetingScrollAnimation};
      animation-delay: 0.5s;
    }
  }

  &.kickoff-meeting-scroll-animate-end {
    ${PhaseImg} {
      bottom: 0;
    }
  }
`;

const RatioInner = styled.div`
  position: absolute;
  inset: 0;
`;

const ImgWrapper = styled.div`
  display: flex;
  justify-content: center;

  width: calc(100% - 64px);
  left: 32px;
  height: 100%;
  position: absolute;
`;

const TextWrapper = styled.div`
  position: absolute;
  bottom: 64px;
  left: 24px;
  width: calc(100% - 40px);
  font-size: 16px;
  font-weight: 400;
  line-height: 1.4;
  opacity: 0;

  ${mediaMin.xs`
    width: 380px;
    left: 64px;
    font-size: 18px;
  `}

  ${mediaMin.sm`
    width: 520px;
  `}
`;

const PhaseInfo = styled.div`
  display: none;
  position: absolute;
  bottom: 6vh;
  text-align: center;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 40px);

  ${mediaMin.xs`
    bottom: 10vh;
    width: 500px;
  `}

  ${mediaMin.md`
    bottom: 48px;
  `}
`;

const PhaseTitle = styled.div`
  font-weight: 500;
  margin-bottom: 24px;
  font-size: 20px;

  ${mediaMin.xs`
    font-size: 22px;
  `}
`;

const PhaseDescription = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.4;

  ${mediaMin.xs`
    font-size: 18px;
  `}

  strong {
    font-weight: 500;
  }
`;

export function SolutionSection() {
  const { t } = useTranslation();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const mainCopyRef = useRef<HTMLDivElement>(null);
  const imgWrapperRef = useRef<HTMLDivElement>(null);
  const ImgTopCoverRef = useRef<HTMLDivElement>(null);
  const ImgBottomCoverRef = useRef<HTMLDivElement>(null);
  const projectImgRef = useRef<HTMLImageElement>(null);
  const contractRef = useRef<HTMLDivElement>(null);
  const kickoffMeetingRef = useRef<HTMLImageElement>(null);
  const inquiryRef = useRef<HTMLImageElement>(null);
  const textWrapperRef = useRef<HTMLDivElement>(null);

  const phaseInfoRef = useRef<HTMLDivElement>(null);
  const phaseTitleRef = useRef<HTMLDivElement>(null);
  const phaseDescriptionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let handleScroll: () => void;
    setTimeout(() => {
      const { innerWidth } = window;
      const xAmount = 100; // project 가 각 phase 로 분화 될 때, phase 간의 거리

      let phaseGap = innerWidth; // project 가 분화된 이후 phase 간의 거리
      if (innerWidth >= sizes.md) {
        phaseGap = 640;
      } else if (innerWidth >= sizes.sm) {
        phaseGap = innerWidth / 1.8;
      } else {
        phaseGap = innerWidth / 1.5;
      }

      let initialPhaseScale = 0.65; // 각 phase image 가 project 에서 처음으로 분화되어나올 때 크기 조절 값
      if (innerWidth > sizes.xs) {
        initialPhaseScale = 0.85;
      }

      const blurPhaseScale = 0.6; // 가로로 나열된 상태에서 뒤쪽에 있는 phase image 의 크기 조절 값

      const wrapperOffsetTop = wrapperRef.current?.offsetTop || 0;

      handleScroll = () => {
        const { innerHeight, pageYOffset: currentScrollTop } = window;
        const startScrollTopOfAnimation = wrapperOffsetTop + innerHeight;
        const scrollMultiples =
          window.innerWidth < sizes.xs
            ? MOBILE_SCROLL_MULTIPLES
            : DESKTOP_SCROLL_MULTIPLES;
        const scrollTops = [
          startScrollTopOfAnimation,
          startScrollTopOfAnimation + innerHeight * scrollMultiples * 2,
          startScrollTopOfAnimation + innerHeight * scrollMultiples * 3,
          startScrollTopOfAnimation + innerHeight * scrollMultiples * 4,
          startScrollTopOfAnimation + innerHeight * scrollMultiples * 5,
          startScrollTopOfAnimation + innerHeight * scrollMultiples * 6,
          startScrollTopOfAnimation + innerHeight * scrollMultiples * 7,
          startScrollTopOfAnimation + innerHeight * scrollMultiples * 8,
          startScrollTopOfAnimation + innerHeight * scrollMultiples * 9,
        ];

        if (
          imgWrapperRef.current &&
          projectImgRef.current &&
          contractRef.current &&
          kickoffMeetingRef.current &&
          inquiryRef.current &&
          ImgTopCoverRef.current &&
          ImgBottomCoverRef.current &&
          mainCopyRef.current &&
          textWrapperRef.current &&
          phaseInfoRef.current &&
          phaseTitleRef.current &&
          phaseDescriptionRef.current
        ) {
          if (currentScrollTop > scrollTops[2]) {
            // 특정 scrollTop 보다 아래에 있는 경우 아래 엘리먼트들을 항상 보이지 않게 처리한다.
            mainCopyRef.current.style.opacity = "0";
            ImgTopCoverRef.current.style.height = "0";
            ImgBottomCoverRef.current.style.height = "0";
          }

          if (currentScrollTop < scrollTops[0]) {
            // MainCopy (Yess streamlines the workflow by automation) 가 처음 보이는 구간
            ImgTopCoverRef.current.style.height = "50%";
            ImgBottomCoverRef.current.style.height = "50%";
            mainCopyRef.current.style.opacity = "1";
            textWrapperRef.current.style.opacity = "";
          } else if (currentScrollTop < scrollTops[1]) {
            // MainCopy 가 사라지고, project launch 이미지가 뷰 중앙으로부터 나타난다. (imgCover 가 없어지는 구간)
            mainCopyRef.current.style.opacity = "0";
            projectImgRef.current.style.transform = `translate(0, calc(-50%))`;

            const scrollRatio =
              (currentScrollTop - scrollTops[0]) / window.innerHeight;
            ImgTopCoverRef.current.style.height = `${
              (window.innerHeight / 2) * (1 - scrollRatio)
            }px`;
            ImgBottomCoverRef.current.style.height = `${
              (window.innerHeight / 2) * (1 - scrollRatio)
            }px`;
            if (scrollRatio > 0.5) {
              textWrapperRef.current.style.opacity = `${
                (scrollRatio - 0.5) * 2
              }`;
            }

            // 아직 phase 상태를 나타내는 이미지가 보여서는 안된다.
            contractRef.current.style.opacity = "0";
            contractRef.current.style.transform = "";

            kickoffMeetingRef.current.style.opacity = "0";
            kickoffMeetingRef.current.style.transform = "";

            inquiryRef.current.style.opacity = "0";
            inquiryRef.current.style.transform = "";
          } else if (currentScrollTop < scrollTops[2]) {
            // project image 가 서서히 비틀어지는 구간
            const scrollRatio =
              (currentScrollTop - scrollTops[1]) /
              (scrollTops[2] - scrollTops[1]);
            projectImgRef.current.style.transform = `skew(${
              scrollRatio * -0.21
            }rad, ${scrollRatio * 0.54}rad) scale(${
              1 - 0.2 * scrollRatio
            }) translate(0, calc(-50% - ${40 * scrollRatio}px))`;

            imgWrapperRef.current.style.transform = "";

            contractRef.current.style.opacity = "0";
            contractRef.current.style.transform = "";

            kickoffMeetingRef.current.style.opacity = "0";
            kickoffMeetingRef.current.style.transform = "";

            inquiryRef.current.style.opacity = "0";
            inquiryRef.current.style.transform = "";
          } else if (currentScrollTop < scrollTops[3]) {
            // project 이미지의 비틀림 이후, 각 phase 의 이미지가 분화되어 나타난다.
            const scrollRatio =
              (currentScrollTop - scrollTops[2]) /
              (scrollTops[3] - scrollTops[2]);
            imgWrapperRef.current.style.transform = `translateX(${
              scrollRatio * 160
            }px)`;

            projectImgRef.current.style.transform = `skew(-0.21rad, 0.54rad) scale(0.8) translate(${
              160 * scrollRatio
            }px, calc(-50% - 40px - ${160 * scrollRatio}px))`;

            contractRef.current.style.transform = `skew(-0.21rad, 0.54rad) translate(${
              -xAmount * scrollRatio
            }px, calc(-50% - 40px + ${
              xAmount * scrollRatio
            }px)) scale(${initialPhaseScale})`;
            contractRef.current.style.opacity = `${0.6 * scrollRatio}`;

            kickoffMeetingRef.current.style.transform = `skew(-0.21rad, 0.54rad) translate(${
              -(xAmount * 2) * scrollRatio
            }px, calc(-50% - 40px + ${
              xAmount * 1.72 * scrollRatio
            }px)) scale(${initialPhaseScale})`;
            kickoffMeetingRef.current.style.opacity = `${0.6 * scrollRatio}`;

            inquiryRef.current.style.transform = `skew(-0.21rad, 0.54rad) translate(${
              -(xAmount * 3) * scrollRatio
            }px, calc(-50% - 40px + ${
              xAmount * 2.44 * scrollRatio
            }px)) scale(${initialPhaseScale})`;
            inquiryRef.current.style.opacity = `${0.6 * scrollRatio}`;
          } else if (currentScrollTop < scrollTops[4]) {
            // 분화된 이미지들의 비틀림이 없어지면서 가로로 정렬된다. 각 이미지는 phaseGap 에 비례한 만큼 간격을 두고 떨어져있게 된다.
            const scrollRatio =
              (currentScrollTop - scrollTops[3]) /
              (scrollTops[4] - scrollTops[3]);
            const reverseScrollRatio = 1 - scrollRatio;
            imgWrapperRef.current.style.transform = `translateX(${
              reverseScrollRatio * 160
            }px)`;

            projectImgRef.current.style.transform = `skew(${
              reverseScrollRatio * -0.21
            }rad, ${reverseScrollRatio * 0.54}rad) scale(${
              0.8 + 0.2 * scrollRatio
            }) translate(${
              phaseGap * 3 * scrollRatio + 160 * reverseScrollRatio
            }px, calc(-50% - 40px - ${160 * reverseScrollRatio}px))`;
            projectImgRef.current.style.opacity = `${1 - 0.4 * scrollRatio}`;

            contractRef.current.style.transform = `skew(${
              reverseScrollRatio * -0.21
            }rad, ${reverseScrollRatio * 0.54}rad) translate(${
              -xAmount * reverseScrollRatio + phaseGap * 2 * scrollRatio
            }px, calc(-50% - 40px + ${
              xAmount * reverseScrollRatio
            }px)) scale(${initialPhaseScale})`;

            kickoffMeetingRef.current.style.transform = `skew(${
              reverseScrollRatio * -0.21
            }rad, ${reverseScrollRatio * 0.54}rad) translate(${
              -(xAmount * 2) * reverseScrollRatio + phaseGap * scrollRatio
            }px, calc(-50% - 40px + ${
              xAmount * 1.72 * reverseScrollRatio
            }px)) scale(${initialPhaseScale})`;

            inquiryRef.current.style.transform = `skew(${
              reverseScrollRatio * -0.21
            }rad, ${reverseScrollRatio * 0.54}rad) translate(${
              -(xAmount * 3) * reverseScrollRatio
            }px, calc(-50% - 40px + ${
              xAmount * 2.44 * reverseScrollRatio
            }px)) scale(${initialPhaseScale})`;
            inquiryRef.current.style.opacity = `${0.6 + 0.4 * scrollRatio}`;

            // 정방향 스크롤에서 걸려있던 transition 을 역방향으로 돌아왔을 떄 없애준다.
            inquiryRef.current.style.transition = "";
            kickoffMeetingRef.current.style.transition = "";
            contractRef.current.style.transition = "";
            projectImgRef.current.style.transition = "";
            textWrapperRef.current.style.display = "";
            phaseInfoRef.current.style.display = "";
          } else if (currentScrollTop < scrollTops[5]) {
            // inquiry 이미지가 확대되고 inquiry 에 대한 설명이 나타난다.
            // transition 할당
            inquiryRef.current.style.transition = `all 0.5s`;
            kickoffMeetingRef.current.style.transition = `all 0.5s`;
            contractRef.current.style.transition = `all 0.5s`;
            projectImgRef.current.style.transition = `all 0.5s`;

            // inquiry 확대, inquiry 설명 문구 나타남, kickoffMeeting 축소
            inquiryRef.current.style.transform = `translate(0px, calc(-50% - 60px)) scale(1.2)`;
            kickoffMeetingRef.current.style.transform = `translate(${phaseGap}px, calc(-50% - 40px)) scale(${blurPhaseScale})`;
            contractRef.current.style.transform = `translate(${
              phaseGap * 2
            }px, calc(-50% - 40px)) scale(${blurPhaseScale})`;
            projectImgRef.current.style.transform = `translate(${
              phaseGap * 3
            }px, calc(-50% - 40px)) scale(${blurPhaseScale})`;

            inquiryRef.current.style.opacity = "1";
            kickoffMeetingRef.current.style.opacity = "0.6";

            textWrapperRef.current.style.display = "none";

            phaseInfoRef.current.style.display = "block";
            phaseTitleRef.current.innerHTML = t(
              "main.solutionSection.inquiryPart.title"
            );
            phaseDescriptionRef.current.innerHTML = t(
              "main.solutionSection.inquiryPart.description"
            );
          } else if (currentScrollTop < scrollTops[6]) {
            // inquiry 축소 및 좌로 이동, kickoffMeeting 확대, kickoffMeeting 설명 문구 나타남
            // kickoffMeeting 이미지의 스크롤 애니메이션을 실행한다.
            inquiryRef.current.style.transform = `translate(-${phaseGap}px, calc(-50% - 40px)) scale(${blurPhaseScale})`;
            kickoffMeetingRef.current.style.transform = `translate(0px, calc(-50% - 60px)) scale(1.2)`;
            contractRef.current.style.transform = `translate(${phaseGap}px, calc(-50% - 40px)) scale(${blurPhaseScale})`;
            projectImgRef.current.style.transform = `translate(${
              phaseGap * 2
            }px, calc(-50% - 40px)) scale(${blurPhaseScale})`;

            if (
              !kickoffMeetingRef.current.classList.contains(
                "kickoff-meeting-scroll-animate"
              ) &&
              !kickoffMeetingRef.current.classList.contains(
                "kickoff-meeting-scroll-animate-end"
              )
            ) {
              kickoffMeetingRef.current.classList.add(
                "kickoff-meeting-scroll-animate"
              );
              setTimeout(() => {
                if (
                  kickoffMeetingRef.current &&
                  currentScrollTop >= scrollTops[5] &&
                  currentScrollTop < scrollTops[6]
                ) {
                  kickoffMeetingRef.current.classList.remove(
                    "kickoff-meeting-scroll-animate"
                  );
                  kickoffMeetingRef.current.classList.add(
                    "kickoff-meeting-scroll-animate-end"
                  );
                }
              }, 5 * 1000);
            }

            inquiryRef.current.style.opacity = "0.6";
            kickoffMeetingRef.current.style.opacity = "1";
            contractRef.current.style.opacity = "0.6";

            phaseTitleRef.current.innerHTML = t(
              "main.solutionSection.kickoffMeetingPart.title"
            );
            phaseDescriptionRef.current.innerHTML = t(
              "main.solutionSection.kickoffMeetingPart.description"
            );
          } else if (currentScrollTop < scrollTops[7]) {
            // kickoffMeeting 축소 및 좌로 이동, proposal 확대, proposal 설명 문구 나타남
            // proposal 이미지의 스크롤 애니메이션을 실행한다.
            inquiryRef.current.style.transform = `translate(-${
              phaseGap * 2
            }px, calc(-50% - 40px)) scale(${blurPhaseScale})`;
            kickoffMeetingRef.current.style.transform = `translate(-${phaseGap}px, calc(-50% - 40px)) scale(${blurPhaseScale})`;
            contractRef.current.style.transform = `translate(0px, calc(-50% - 60px)) scale(1.2)`;
            contractRef.current.style.opacity = "1";
            projectImgRef.current.style.transform = `translate(${phaseGap}px, calc(-50% - 40px)) scale(${blurPhaseScale})`;

            kickoffMeetingRef.current.classList.remove(
              "kickoff-meeting-scroll-animate",
              "kickoff-meeting-scroll-animate-end"
            );

            if (
              !contractRef.current.classList.contains(
                "contract-scroll-animate"
              ) &&
              !contractRef.current.classList.contains(
                "contract-scroll-animate-end"
              )
            ) {
              contractRef.current.classList.add("contract-scroll-animate");
              setTimeout(() => {
                if (
                  contractRef.current &&
                  currentScrollTop >= scrollTops[6] &&
                  currentScrollTop < scrollTops[7]
                ) {
                  contractRef.current.classList.remove(
                    "contract-scroll-animate"
                  );
                  contractRef.current.classList.add(
                    "contract-scroll-animate-end"
                  );
                }
              }, 5 * 1000);
            }

            kickoffMeetingRef.current.style.opacity = "0.6";
            contractRef.current.style.opacity = "1";
            projectImgRef.current.style.opacity = "0.6";

            phaseTitleRef.current.innerHTML = t(
              "main.solutionSection.contractPart.title"
            );
            phaseDescriptionRef.current.innerHTML = t(
              "main.solutionSection.contractPart.description"
            );
          } else if (currentScrollTop < scrollTops[8]) {
            // proposal 축소 및 좌로 이동, project 확대, project 설명 문구 나타남
            inquiryRef.current.style.transform = `translate(-${
              phaseGap * 3
            }px, calc(-50% - 40px)) scale(${blurPhaseScale})`;
            kickoffMeetingRef.current.style.transform = `translate(-${
              phaseGap * 2
            }px, calc(-50% - 40px)) scale(${blurPhaseScale})`;
            contractRef.current.style.transform = `translate(-${phaseGap}px, calc(-50% - 40px)) scale(${blurPhaseScale})`;
            projectImgRef.current.style.transform = `translate(0px, calc(-50% - 60px)) scale(1)`;
            projectImgRef.current.style.opacity = "1";

            contractRef.current.style.opacity = "0.6";
            projectImgRef.current.style.opacity = "1";

            contractRef.current.classList.remove(
              "contract-scroll-animate",
              "contract-scroll-animate-end"
            );

            phaseTitleRef.current.innerHTML = t(
              "main.solutionSection.launchedProjectPart.title"
            );
            phaseDescriptionRef.current.innerHTML = t(
              "main.solutionSection.launchedProjectPart.description"
            );
          }
        }
      };
      document.addEventListener("scroll", handleScroll, false);
    }, 500);
    return () => {
      document.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <Wrapper ref={wrapperRef}>
      <Page>
        <MainCopy ref={mainCopyRef}>
          <Trans i18nKey="main.solutionSection.mainCopy" />
        </MainCopy>
        <ImgWrapper ref={imgWrapperRef}>
          <ProjectImg ref={projectImgRef} src={projectImageSrc} />
          <RatioContainer ref={contractRef}>
            <RatioInner>
              <PhaseImg src={contractImageSrc} />
            </RatioInner>
          </RatioContainer>
          <RatioContainer ref={kickoffMeetingRef}>
            <RatioInner>
              <PhaseImg src={kickoffMeetingImageSrc} />
            </RatioInner>
          </RatioContainer>
          <RatioContainer ref={inquiryRef}>
            <RatioInner>
              <PhaseImg src={inquiryImageSrc} />
            </RatioInner>
          </RatioContainer>
          <ImgTopCover ref={ImgTopCoverRef} />
          <ImgBottomCover ref={ImgBottomCoverRef} />
        </ImgWrapper>
        <PhaseInfo ref={phaseInfoRef}>
          <PhaseTitle ref={phaseTitleRef} />
          <PhaseDescription ref={phaseDescriptionRef} />
        </PhaseInfo>
        <TextWrapper ref={textWrapperRef}>
          <Trans i18nKey="main.solutionSection.description" />
        </TextWrapper>
      </Page>
    </Wrapper>
  );
}
